import { HttpClient } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { environment } from '@et/environment';
import { isOnline } from '@et/utils';

/** Mirrors the server's `emailAndPassword.minPasswordLength` (src/auth.ts, better-auth-standalone). */
const MIN_PASSWORD_LENGTH = 12;

function passwordsMatchValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    // Don't flag mismatch while the confirm field is still empty — that would
    // hide the required error and leave the submit button disabled with no cue.
    if (!confirmPassword) {
      return null;
    }
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  };
}

@Component({
  selector: 'auth-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: false,
})
export class ResetPasswordComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);

  /** Exposed for the template min-length hint. */
  readonly minPasswordLength = MIN_PASSWORD_LENGTH;

  form: UntypedFormGroup = this.fb.group(
    {
      newPassword: [
        null,
        [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)],
      ],
      confirmPassword: [null, [Validators.required]],
    },
    { validators: passwordsMatchValidator() },
  );

  submitted = false;
  /** No/expired token in the query string — hide the form, offer a fresh link instead. */
  invalidToken = false;
  resetComplete = false;
  private token: string | null = null;

  ngOnInit() {
    // Better Auth's GET /reset-password/:token redirect appends `?token=...` to
    // `callbackURL` once the token validates server-side (see the plan doc §2) —
    // a missing token here means the link was already invalid/expired.
    this.token = this.route.snapshot.queryParamMap.get('token');
    if (!this.token) {
      this.invalidToken = true;
    }
  }

  async resetPassword(newPassword: string) {
    if (!this.token) {
      this.invalidToken = true;
      return;
    }
    if (!isOnline()) {
      this.form.get('newPassword')?.setErrors({ isOffline: true });
      return;
    }
    this.submitted = true;
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.betterAuth.baseUrl}/api/auth/reset-password`,
          { newPassword, token: this.token },
          { withCredentials: true },
        ),
      );
      this.resetComplete = true;
    } catch {
      // Expired/invalid token, or a transport failure — either way this token
      // can't be retried; send the user to request a fresh link.
      this.invalidToken = true;
    } finally {
      this.submitted = false;
    }
  }

  goToLogin() {
    this.router.navigateByUrl('/auth/login');
  }
}
